import type React from 'react';
import { useTranslation } from 'react-i18next';
import { ListBox } from '@heroui/react';
import { GridLayout, Size, Virtualizer } from 'react-aria-components';
import { getNodeTestIdProp } from '@/utils';
import type { PosProduct } from '../../posProduct';
import { ProductTile } from './ProductTile';
import { useProductGridKeyboard } from './useProductGridKeyboard';
import { useProductGridActivation } from './useProductGridActivation';

type ProductGridProps = {
    products: PosProduct[];
    /** Units of a product in the cart, across all of its variants. */
    inCartQty: (productId: string) => number;
    /** A tile's own tap/press/Enter: adds a single-variant product, opens the picker for a several-variant one. */
    onSelectProduct: (product: PosProduct) => 'added' | 'variantPicker';
    /** An add by mouse or touch: the caller sends focus back to search. A keyboard add does not — the tile keeps focus. */
    onProductAddedByPointer: () => void;
    /** A letter or digit typed anywhere in the grid: redirect it to search. */
    onPrintableKey: (key: string) => void;
    /**
     * The list box's own DOM node. `.focus()` on it (never a query for a
     * specific tile — a scrolled virtualized grid may not have one in the
     * DOM) delegates to react-aria's own collection focus: the first tile on
     * a fresh grid, the previously-focused one otherwise, scrolled into view
     * first if it isn't currently rendered.
     */
    ref?: React.Ref<HTMLDivElement>;
};

/**
 * A fixed height keeps every row the same height regardless of a tile's
 * content (min = max) and the grid virtualized (bos-seller#48 spike): with
 * 1,000 tiles, a non-virtualized grid re-rendered every one of them on each
 * arrow-key press (320-575ms); `Virtualizer` + `GridLayout` keeps only the
 * visible rows (~45 tiles) in the DOM, at 35-40ms per press. Column count
 * still adapts to the panel's width between `minItemSize`/`maxItemSize`.
 */
const PRODUCT_GRID_LAYOUT_OPTIONS = {
    minItemSize: new Size(150, 208),
    maxItemSize: new Size(260, 208),
    minSpace: new Size(12, 12),
};

/**
 * The product grid (bos-seller#55): a virtualized design-system `ListBox` in
 * grid layout, replacing the hand-built native button tiles. One Tab stop;
 * react-aria's own roving focus moves it in two dimensions with the arrow
 * keys, `Home`/`End`, and `Enter` activates the focused tile. Out-of-stock
 * tiles are `isDisabled`, which the design system already renders as
 * disabled and keeps out of activation for every input method.
 *
 * Two behaviours this grid needs beyond what the list box gives for free:
 * - A pointer/touch add sends focus home; a keyboard add keeps focus on the
 *   tile. `useProductGridActivation` tells the two apart.
 * - Typeahead is off: `useProductGridKeyboard`'s capture-phase handler on
 *   the wrapping element redirects a printable key to search before the
 *   list box's own native typeahead ever sees it.
 */
export const ProductGrid: React.FC<ProductGridProps> = ({
    products,
    inCartQty,
    onSelectProduct,
    onProductAddedByPointer,
    onPrintableKey,
    ref,
}) => {
    const { t } = useTranslation('pages');
    const { onGridKeyDownCapture } = useProductGridKeyboard({ onPrintableKey });
    const activation = useProductGridActivation({
        onSelectProduct,
        onProductAddedByPointer,
        onKeyDownCapture: onGridKeyDownCapture,
    });

    return (
        <div
            className="h-full"
            onKeyDownCapture={activation.onGridKeyDownCapture}
            onPointerDownCapture={activation.onGridPointerDownCapture}
        >
            <Virtualizer
                layout={GridLayout}
                layoutOptions={PRODUCT_GRID_LAYOUT_OPTIONS}
            >
                <ListBox
                    aria-label={t('seller.productGrid')}
                    className="h-full overflow-auto"
                    layout="grid"
                    ref={ref}
                    selectionMode="none"
                    {...getNodeTestIdProp('seller-product-grid')}
                >
                    {products.map((product) => (
                        <ListBox.Item
                            className="h-full w-full rounded-2xl border border-border bg-surface p-3 transition hover:border-accent/40 hover:bg-surface-secondary data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[focus-visible]:outline data-[focus-visible]:outline-2 data-[focus-visible]:outline-offset-2 data-[focus-visible]:outline-accent"
                            id={product.id}
                            isDisabled={product.stock <= 0}
                            key={product.id}
                            onAction={() => activation.onProductAction(product)}
                            textValue={product.name}
                            {...getNodeTestIdProp(
                                `seller-product-tile-${product.id}`,
                            )}
                        >
                            <ProductTile
                                inCartQty={inCartQty(product.id)}
                                product={product}
                            />
                        </ListBox.Item>
                    ))}
                </ListBox>
            </Virtualizer>
        </div>
    );
};
